import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/asset-task-ui.css";
import { useAppContext } from "../context/AppContext";
import {
  getAssetTaskById,
  getAssetTasksByProcessId,
  getProcessTasks,
} from "../services/assetTaskService";
import { getTasksByCompanyId } from "../services/taskService";
import { getProcessesByCompanyId } from "../services/processService";
import { toast } from "../utils/toast";
import { useParams } from "react-router-dom";
import { resolveRoleName } from "../utils/auth";

const AssetTaskDetailPage = () => {
  const { assetTaskId, setAssetTaskId, setActiveSection } = useAppContext();
  const { assetTaskId: assetTaskIdParam } = useParams();
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [processes, setProcesses] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [chainTasks, setChainTasks] = useState([]);
  const [processAssets, setProcessAssets] = useState([]);

  const [currentAsset, setCurrentAsset] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const didInit = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  const onBack = () => setActiveSection("asset-task-management");

  const getTaskName = (taskId, fromChain = null) => {
    if (fromChain) {
      const direct =
        fromChain.task_name || fromChain.name || fromChain.task?.task_name;
      if (direct) return direct;
    }
    const idNum = Number(taskId);
    const t = tasks.find(
      (x) => Number(x.task_id ?? x.id ?? x.taskId) === idNum,
    );
    return t?.task_name || t?.name || `Task #${taskId}`;
  };

  const getProcessName = (processId) => {
    const p = processes.find((x) => x.process_id === processId);
    return (
      p?.process_name || p?.name || (processId ? `Process #${processId}` : "-")
    );
  };

  const orderedAssets = useMemo(() => {
    if (!Array.isArray(chainTasks) || chainTasks.length === 0)
      return processAssets || [];
    const byTaskId = new Map();
    (processAssets || []).forEach((a) => {
      const k = Number(a.task_id);
      const list = byTaskId.get(k) || [];
      list.push(a);
      byTaskId.set(k, list);
    });
    const out = [];
    chainTasks.forEach((ct) => {
      const tid = Number(ct.task_id);
      const list = (byTaskId.get(tid) || []).slice();
      // Inputs first (include multiple), then outputs
      const inputs = list.filter((a) => a.input);
      const outputs = list.filter((a) => a.output);
      inputs.forEach((a) => out.push(a));
      outputs.forEach((a) => out.push(a));
    });
    return out;
  }, [chainTasks, processAssets]);

  useEffect(() => {
    const idx = orderedAssets.findIndex(
      (a) => Number(a.asset_task_id) === Number(currentAsset?.asset_task_id),
    );
    if (idx >= 0) setCurrentIndex(idx);
  }, [orderedAssets, currentAsset]);

  useEffect(() => {
    (async () => {
      if (didInit.current) return;
      didInit.current = true;
      try {
        setLoading(true);
        let id = assetTaskId;
        if (assetTaskIdParam) {
          const decoded = decodeURIComponent(assetTaskIdParam);
          id = decoded;
          try {
            localStorage.setItem("activeAssetTaskId", String(decoded));
          } catch {}
          setAssetTaskId(decoded);
        }
        if (!id) {
          try {
            const stored = localStorage.getItem("activeAssetTaskId");
            if (stored) {
              id = stored;
              setAssetTaskId(stored);
            }
          } catch {}
        }
        if (!id) throw new Error("Missing asset task id");

        const data = await getAssetTaskById(id);
        setCurrentAsset(data);

        // Load names/chain using company + process
        const companyId = data?.task?.company_id || data?.process?.company_id;
        const processId = data?.process_id || data?.process?.process_id;
        if (companyId) {
          const ps = await getProcessesByCompanyId(companyId);
          setProcesses(Array.isArray(ps) ? ps : []);
          const ts = await getTasksByCompanyId(companyId);
          setTasks(Array.isArray(ts) ? ts : []);
        }
        if (processId) {
          const chain = await getProcessTasks(processId);
          if (Array.isArray(chain)) setChainTasks(chain);
          const assets = await getAssetTasksByProcessId(processId);
          setProcessAssets(Array.isArray(assets) ? assets : []);
        }
      } catch (e) {
        setError(e?.message || "Failed to load asset task");
        toast.error(e?.message || "Failed to load asset task");
      } finally {
        setLoading(false);
      }
    })();
  }, [assetTaskId, assetTaskIdParam, setAssetTaskId]);

  useEffect(() => {
    // When users navigate, update currentAsset
    if (
      orderedAssets.length > 0 &&
      currentIndex >= 0 &&
      currentIndex < orderedAssets.length
    ) {
      setCurrentAsset(orderedAssets[currentIndex]);
    }
  }, [currentIndex, orderedAssets]);

  const onPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const onNext = () =>
    setCurrentIndex((i) => Math.min(orderedAssets.length - 1, i + 1));

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Asset Task Details</h2>
            <p className="page-subtitle">
              {orderedAssets.length > 0 && (
                <span className="nav-counter">
                  <svg
                    style={{ width: "14px", height: "14px" }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  Viewing {currentIndex + 1} of {orderedAssets.length}
                </span>
              )}
            </p>
          </div>
          <div className="roles-toolbar">
            <button
              className="secondary-btn"
              onClick={onBack}
              disabled={loading}
            >
              Back
            </button>
            <button
              className="secondary-btn"
              onClick={onPrev}
              disabled={loading || currentIndex <= 0}
            >
              Prev
            </button>
            <button
              className="primary-btn"
              onClick={onNext}
              disabled={loading || currentIndex >= orderedAssets.length - 1}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {error && <div className="error-banner">{error}</div>}
        {loading ?
          <div className="no-results">Loading...</div>
        : !currentAsset ?
          <div className="no-results">Asset not found</div>
        : <>
            <div className="detail-card">
              <div className="detail-header">
                <div className="detail-title-group">
                  <div className="detail-icon">
                    <svg
                      style={{ width: "18px", height: "18px" }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="detail-title">Asset Information</h3>
                </div>
                <div className="detail-badges">
                  <span
                    className={`type-badge ${
                      currentAsset.type || "information"
                    }`}
                  >
                    <svg
                      style={{ width: "14px", height: "14px" }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      {currentAsset.type === "event" ?
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                        />
                      : currentAsset.type === "document" ?
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      : <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      }
                    </svg>
                    {currentAsset.type || "Information"}
                  </span>
                  <span
                    className={`position-badge ${
                      currentAsset.input ? "input"
                      : currentAsset.output ? "output"
                      : ""
                    }`}
                  >
                    <svg
                      style={{ width: "14px", height: "14px" }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      {currentAsset.input ?
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16l-4-4m0 0l4-4m-4 4h18"
                        />
                      : <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      }
                    </svg>
                    {currentAsset.input ?
                      "Input"
                    : currentAsset.output ?
                      "Output"
                    : "-"}
                  </span>
                </div>
              </div>

              <div className="form-grid">
                <div className="detail-field">
                  <label className="detail-field-label">Process</label>
                  <div className="detail-field-value">
                    {getProcessName(currentAsset.process_id)}
                  </div>
                </div>
                <div className="detail-field">
                  <label className="detail-field-label">Task</label>
                  <div className="detail-field-value">
                    {currentAsset?.task?.task_name ||
                      getTaskName(Number(currentAsset.task_id))}
                  </div>
                </div>
                {currentAsset.type === "document" && (
                  <div className="detail-field full">
                    <label className="detail-field-label">Document Link</label>
                    {currentAsset.document_link ?
                      <div className="document-link-display">
                        <svg
                          className="document-link-icon"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                        <a
                          href={currentAsset.document_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {currentAsset.document_link}
                        </a>
                      </div>
                    : <div className="detail-field-value">No link provided</div>
                    }
                  </div>
                )}
                <div className="detail-field full">
                  <label className="detail-field-label">Description</label>
                  <div className="detail-field-value large">
                    {currentAsset.description || "-"}
                  </div>
                </div>
              </div>
            </div>

            {Array.isArray(chainTasks) && chainTasks.length > 0 && (
              <div className="detail-card">
                <div className="card-header">
                  <div className="card-header-icon">
                    <svg
                      style={{ width: "16px", height: "16px" }}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <h3 className="card-header-title">Process Task Chain</h3>
                </div>
                <div className="chain-display">
                  {chainTasks.map((ct, idx) => (
                    <React.Fragment key={idx}>
                      <div className="chain-step">
                        <div className="chain-step-number">
                          {ct.order || idx + 1}
                        </div>
                        <div className="chain-step-content">
                          <div className="chain-step-title">
                            {getTaskName(ct.task_id, ct)}
                          </div>
                        </div>
                      </div>
                      {idx < chainTasks.length - 1 && (
                        <div className="chain-arrow">↓</div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </>
        }
      </div>
    </div>
  );
};

export default AssetTaskDetailPage;
